import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Bell, Package, ChevronRight, Inbox } from "lucide-react";
import { api } from "../../store/slices/authSlice";
import styles from "./NotificationPage.module.css";

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNotifications = async () => {
      setLoading(true);
      try {
        const res = await api.get("/notifications");
        if (res.data.code === 200) {
          setNotifications(res.data.data || []);
        }
      } catch (err) {
        console.error("Notifications fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchNotifications();
  }, []);

  const handleNotifClick = (notif) => {
    const meta = notif.metadata;
    if (
      meta?.type === "supplier_material_purchase_order" &&
      meta?.purchase_order_id
    ) {
      navigate(`/orders/${meta.purchase_order_id}`);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-EG", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case "accepted":
        return { color: "#0c6839", bg: "rgba(12,104,57,0.1)" };
      case "rejected":
        return { color: "#ef4444", bg: "rgba(239,68,68,0.1)" };
      default:
        return { color: "#f97316", bg: "rgba(249,115,22,0.1)" };
    }
  };

  const getNotifText = (notif) => {
    if (notif.body) return notif.body;
    if (notif.data?.message || notif.message)
      return notif.data?.message || notif.message;
    const meta = notif.metadata;
    if (meta?.purchase_order_id && meta?.status) {
      const label = meta.status.charAt(0).toUpperCase() + meta.status.slice(1);
      return `Your order #PO-${meta.purchase_order_id} has been ${label}`;
    }
    return "New notification";
  };

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}>
          <ArrowLeft size={18} />
        </button>
        <h1 className={styles.pageTitle}>Notifications</h1>
        {notifications.length > 0 && (
          <span className={styles.countBadge}>{notifications.length}</span>
        )}
      </div>

      <div className={styles.content}>
        {loading ? (
          <div className={styles.skeletonList}>
            {[...Array(4)].map((_, i) => (
              <div key={i} className={styles.skeleton} />
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <div className={styles.empty}>
            <Inbox size={52} strokeWidth={1.2} color="#d1d5db" />
            <p>No notifications yet</p>
          </div>
        ) : (
          <div className={styles.list}>
            {notifications.map((notif, i) => {
              const meta = notif.metadata;
              const isClickable =
                meta?.type === "supplier_material_purchase_order" &&
                meta?.purchase_order_id;
              const statusStyle = meta?.status
                ? getStatusStyle(meta.status)
                : null;
              const statusLabel = meta?.status
                ? meta.status.charAt(0).toUpperCase() + meta.status.slice(1)
                : null;

              return (
                <div
                  key={notif.id || i}
                  className={`${styles.notifCard} ${isClickable ? styles.clickable : ""} ${!notif.read_at ? styles.unread : ""}`}
                  onClick={() => isClickable && handleNotifClick(notif)}
                >
                  {!notif.read_at && <div className={styles.unreadDot} />}

                  <div className={styles.iconWrap}>
                    <Package size={20} color="#0c6839" />
                  </div>

                  <div className={styles.textWrap}>
                    <p className={styles.notifText}>{getNotifText(notif)}</p>

                    <div className={styles.notifFooter}>
                      {meta?.purchase_order_id && (
                        <span className={styles.orderIdBadge}>
                          PO-{meta.purchase_order_id}
                        </span>
                      )}
                      {statusLabel && statusStyle && (
                        <span
                          className={styles.statusBadge}
                          style={{
                            color: statusStyle.color,
                            background: statusStyle.bg,
                          }}
                        >
                          {statusLabel}
                        </span>
                      )}
                      <span className={styles.dateText}>
                        {formatDate(notif.created_at)}
                      </span>
                    </div>
                  </div>

                  {isClickable && (
                    <ChevronRight
                      size={16}
                      color="#9ca3af"
                      className={styles.chevron}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
